import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Divider,
    Group,
    Loader,
    Paper,
    Select,
    Stack,
    Switch,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useGetFacilityRegistrationFields } from '../../../global/entities/RegistrationFields/queries/useGetFacilityRegistrationFields.tsx';
import { useSaveFacilityRegistrationFields } from '../../../global/entities/RegistrationFields/mutations/useSaveFacilityRegistrationFields.tsx';
import {
    IRegistrationFieldDefinition,
    isValidRegistrationDefault,
    RegistrationFieldDefaultType,
} from '../../../global/entities/RegistrationFields/RegistrationFields.ts';

interface HcfRegistrationFieldsPanelProps {
    facilityId: number;
}

interface FieldState {
    hidden: boolean;
    defaultValue: string;
}

export default function HcfRegistrationFieldsPanel({ facilityId }: HcfRegistrationFieldsPanelProps) {
    const { t } = useTranslation();
    const { data, isLoading } = useGetFacilityRegistrationFields(facilityId);
    const saveMutation = useSaveFacilityRegistrationFields();
    const [states, setStates] = useState<Record<string, FieldState>>({});

    useEffect(() => {
        if (!data) return;
        const next: Record<string, FieldState> = {};
        data.availableFields.forEach((field) => {
            const saved = data.config.find((c) => c.fieldKey === field.fieldKey);
            next[field.fieldKey] = {
                hidden: saved?.hidden ?? false,
                defaultValue: saved?.defaultValue ?? '',
            };
        });
        setStates(next);
    }, [data]);

    const sexOptions = useMemo(
        () => (data?.sexes ?? []).map((s) => ({ value: s.id.toString(), label: s.name })),
        [data],
    );

    const setField = (fieldKey: string, patch: Partial<FieldState>) =>
        setStates((prev) => ({ ...prev, [fieldKey]: { ...prev[fieldKey], ...patch } }));

    const needsDefault = (field: IRegistrationFieldDefinition, state: FieldState) =>
        state.hidden && field.mandatory && field.defaultType !== 'None';

    const isFieldInvalid = (field: IRegistrationFieldDefinition, state: FieldState) =>
        needsDefault(field, state) && !isValidRegistrationDefault(field.defaultType, state.defaultValue);

    const hasMissingDefaults = (data?.availableFields ?? []).some((field) =>
        isFieldInvalid(field, states[field.fieldKey] ?? { hidden: false, defaultValue: '' }),
    );

    const handleSave = () => {
        if (!data || hasMissingDefaults) return;
        const config = data.availableFields.map((field) => {
            const state = states[field.fieldKey];
            return {
                fieldKey: field.fieldKey,
                hidden: state.hidden,
                defaultValue: needsDefault(field, state) ? state.defaultValue : undefined,
            };
        });
        saveMutation.mutate({ facilityId, config });
    };

    const renderDefaultControl = (
        type: RegistrationFieldDefaultType,
        value: string,
        onChange: (v: string) => void,
        invalid: boolean,
    ) => {
        const error = invalid ? t('registrationFields.admin.defaultRequired') : undefined;
        switch (type) {
            case 'Sex':
                return (
                    <Select
                        label={t('registrationFields.admin.defaultValue')}
                        data={sexOptions}
                        value={value || null}
                        onChange={(v) => onChange(v ?? '')}
                        error={error}
                    />
                );
            case 'Boolean':
                return (
                    <Select
                        label={t('registrationFields.admin.defaultValue')}
                        data={[
                            { value: 'true', label: t('registrationFields.admin.yes') },
                            { value: 'false', label: t('registrationFields.admin.no') },
                        ]}
                        value={value || null}
                        onChange={(v) => onChange(v ?? '')}
                        error={error}
                    />
                );
            case 'Date':
                return (
                    <TextInput
                        label={t('registrationFields.admin.defaultValue')}
                        placeholder="1900-01-01"
                        value={value}
                        onChange={(e) => onChange(e.currentTarget.value)}
                        error={error}
                    />
                );
            case 'PostalCode':
                return (
                    <TextInput
                        label={t('registrationFields.admin.defaultValue')}
                        value={value}
                        onChange={(e) => onChange(e.currentTarget.value)}
                        error={error}
                    />
                );
            default:
                return null;
        }
    };

    if (isLoading || !data) return <Loader />;

    return (
        <Paper shadow="sm" radius="md" p="md">
            <Stack>
                <Title order={3}>{t('registrationFields.admin.title')}</Title>
                <Text size="sm" c="dimmed">
                    {t('registrationFields.admin.description')}
                </Text>

                {data.availableFields.map((field) => {
                    const state = states[field.fieldKey] ?? { hidden: false, defaultValue: '' };
                    const invalid = isFieldInvalid(field, state);
                    return (
                        <Stack key={field.fieldKey} gap="xs">
                            <Divider />
                            <Group justify="space-between" align="center">
                                <Text fw={600}>{t(`registrationFields.field.${field.fieldKey}`)}</Text>
                                <Switch
                                    label={t('registrationFields.admin.hide')}
                                    checked={state.hidden}
                                    onChange={(e) =>
                                        setField(field.fieldKey, { hidden: e.currentTarget.checked })
                                    }
                                />
                            </Group>
                            {needsDefault(field, state) &&
                                renderDefaultControl(
                                    field.defaultType,
                                    state.defaultValue,
                                    (v) => setField(field.fieldKey, { defaultValue: v }),
                                    invalid,
                                )}
                        </Stack>
                    );
                })}

                {hasMissingDefaults && (
                    <Alert color="red" icon={<IconAlertCircle />}>
                        {t('registrationFields.admin.missingDefaults')}
                    </Alert>
                )}

                <Group justify="flex-end">
                    <Button
                        onClick={handleSave}
                        loading={saveMutation.isPending}
                        disabled={hasMissingDefaults}
                    >
                        {t('registrationFields.admin.save')}
                    </Button>
                </Group>
            </Stack>
        </Paper>
    );
}
